import { parseErrorEnvelope, parseSessionState, type SessionState } from '@saldo/contracts';

/**
 * The session wire client (P2-T2).
 *
 * It speaks the surface P2-T1 built and nothing else:
 *
 *   GET    /api/v1/session    bootstrap: bounded anonymous state, or the current session
 *   POST   /api/v1/sessions   login `{email,password}`
 *   DELETE /api/v1/session    logout, `204`
 *
 * Two contract details are consumed, not re-decided here:
 *
 * - the CSRF token travels in the **response body** of a bootstrap and comes back in
 *   exactly one `X-CSRF-Token` header, so it lives in this tab's memory only — never
 *   in a URL, a cookie the script can read, or any storage the browser keeps across
 *   reloads (register §5, "CSRF"; IMPLEMENTATION-CONTRACTS §7);
 * - every failure arrives as the shared error envelope, so the status is classified
 *   and the envelope's own safe code is kept for the log line, never rendered.
 *
 * No token is written anywhere and no response is cached: the API answers the session
 * surface with `Cache-Control: no-store`, and this client does not put a copy in
 * `localStorage`, `sessionStorage`, IndexedDB or a cookie.
 */

/** The session-state resource, as the API's schema defines it. */
export type SessionData = SessionState['data'];

export const SESSION_PATH = '/api/v1/session';
export const SESSIONS_PATH = '/api/v1/sessions';
export const CSRF_HEADER = 'X-CSRF-Token';

/**
 * What a failed session request means to the shell.
 *
 * `unauthenticated` is the 401 branch. The API uses it for both "no session" and
 * "credentials refused", so the *call site* decides the wording — and, for a private
 * request, it is the signal that the session is gone: private data is hidden and
 * only the same-owner in-memory draft is kept (IMPLEMENTATION-CONTRACTS §6).
 */
export type SessionFailureKind = 'unauthenticated' | 'rejected' | 'throttled' | 'unavailable';

/** A status of 0 means the request never got an answer. */
export function classifySessionFailure(status: number): SessionFailureKind {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'rejected';
  if (status === 429) return 'throttled';
  return 'unavailable';
}

export type FailureContext = 'sign-in' | 'session';

/**
 * The rule the boundary acts on for a private request: **only a 401 ends the session.**
 *
 * A 403 is a refused *request* (a stale CSRF token or a provenance rejection), not a
 * refused session — signing the user out on one would turn a transport blip into lost
 * work. 429 and 5xx are transient. This is a named rule rather than a condition
 * inline in the component so it is asserted directly.
 */
export function sessionLost(kind: SessionFailureKind): boolean {
  return kind === 'unauthenticated';
}

/** The sentence the user sees. One generic line per kind: no server text is echoed. */
export function describeSessionFailure(kind: SessionFailureKind, context: FailureContext): string {
  switch (kind) {
    case 'unauthenticated':
      return context === 'sign-in'
        ? 'That email and password were not accepted.'
        : 'Your session ended. Sign in again to continue.';
    case 'rejected':
      return 'The request was refused. Reload the page and try again.';
    case 'throttled':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'unavailable':
      return 'The server could not be reached. Try again.';
  }
}

export class SessionRequestError extends Error {
  override readonly name = 'SessionRequestError';
  readonly kind: SessionFailureKind;
  readonly status: number;
  /** The envelope's own safe code, for the log line. Never rendered. */
  readonly code: string | undefined;
  readonly retryable: boolean;

  constructor(
    kind: SessionFailureKind,
    status: number,
    options: { code?: string | undefined; retryable?: boolean } = {},
  ) {
    super(`session request failed (${kind})`);
    this.kind = kind;
    this.status = status;
    this.code = options.code;
    this.retryable = options.retryable ?? false;
  }

  /** The request never reached the API. */
  static unreachable(): SessionRequestError {
    return new SessionRequestError('unavailable', 0);
  }
}

async function failureOf(response: Response): Promise<SessionRequestError> {
  let code: string | undefined;
  let retryable = false;
  try {
    const envelope = parseErrorEnvelope(await response.json());
    code = envelope.error.code;
    retryable = envelope.error.retryable;
  } catch {
    // Not the application's envelope (a proxy page, an empty body): the status is
    // still the answer, and nothing from it is rendered.
  }
  return new SessionRequestError(classifySessionFailure(response.status), response.status, {
    code,
    retryable,
  });
}

async function send(
  path: string,
  init: { method: string; csrfToken: string; body?: unknown; signal?: AbortSignal | undefined },
): Promise<Response> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    [CSRF_HEADER]: init.csrfToken,
  };
  if (init.body !== undefined) headers['content-type'] = 'application/json';

  try {
    return await fetch(path, {
      method: init.method,
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      // The session cookie is same-origin and HttpOnly; this is stated rather than
      // inherited so a later cross-origin base cannot silently change it.
      credentials: 'same-origin',
      signal: init.signal,
    });
  } catch (error) {
    if (init.signal?.aborted === true) throw error;
    throw SessionRequestError.unreachable();
  }
}

/** Bootstrap or read the current session. Also mints the CSRF token a login needs. */
export async function readSession(signal?: AbortSignal): Promise<SessionData> {
  let response: Response;
  try {
    response = await fetch(SESSION_PATH, {
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      signal,
    });
  } catch (error) {
    if (signal?.aborted === true) throw error;
    throw SessionRequestError.unreachable();
  }
  if (!response.ok) throw await failureOf(response);
  return parseSessionState(await response.json()).data;
}

export async function signIn(
  credentials: { email: string; password: string },
  csrfToken: string,
  signal?: AbortSignal,
): Promise<SessionData> {
  const response = await send(SESSIONS_PATH, {
    method: 'POST',
    csrfToken,
    body: credentials,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSessionState(await response.json()).data;
}

/** Revoke the session server-side; the API answers `204` and clears the cookie. */
export async function signOut(csrfToken: string, signal?: AbortSignal): Promise<void> {
  const response = await send(SESSION_PATH, { method: 'DELETE', csrfToken, signal });
  if (!response.ok) throw await failureOf(response);
}
